import React, { useCallback } from 'react';
import Avatar from '@atlaskit/avatar';
import Button, { LoadingButton } from '@atlaskit/button';
import DeleteIcon from '@atlaskit/icon/core/delete';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';
import { Box, Flex, Text } from '@atlaskit/primitives';
import ToolTip from '@atlaskit/tooltip';
import { useOpenClose } from '@css-xp/ppc-custom-hooks';
import { Admin } from './billing-admins';

interface BillingAdminsRemoveButtonProps {
  billingAdmin: Admin;
  isDisabled: boolean;
  isLoading: boolean;
  onRemoveAdmin: (email: string) => void;
}

export const BillingAdminsRemoveButton = ({
  billingAdmin,
  isDisabled,
  isLoading,
  onRemoveAdmin,
}: BillingAdminsRemoveButtonProps) => {
  const { isOpen: isModalOpen, open: openModal, close: closeModal } = useOpenClose();

  const handleRemoveClick = useCallback(() => {
    openModal();
  }, [openModal]);

  const handleConfirmClick = useCallback(() => {
    onRemoveAdmin(billingAdmin.email);
    closeModal();
  }, [onRemoveAdmin, billingAdmin.email, closeModal]);

  return (
    <>
      <Box paddingInline="space.100">
        <ToolTip content="Remove">
          <Button
            appearance="subtle"
            onClick={handleRemoveClick}
            iconAfter={<DeleteIcon label="Remove" />}
            aria-label="Remove"
            testId="billing-admin-remove-action-btn"
            isDisabled={isDisabled}
          />
        </ToolTip>
      </Box>
      <ModalTransition>
        {isModalOpen && (
          <Modal onClose={closeModal} testId="billing-admin-remove-modal" width={500}>
            <ModalHeader>
              <ModalTitle>Confirm removal</ModalTitle>
            </ModalHeader>
            <ModalBody>
              <Flex direction="column" gap="space.200">
                <Text>You are about to remove this billing administrator from this account.</Text>
                <Flex gap="space.100" alignItems="center">
                  <Avatar src={billingAdmin.picture} size="large" />
                  <Flex direction="column">
                    <Text>{billingAdmin.fullName}</Text>
                    <Text size="small" color="color.text.subtle">
                      {billingAdmin.email}
                    </Text>
                  </Flex>
                </Flex>
                <Text>
                  This individual will no longer have access to the Partner Purchasing Center and its associated
                  functions. Are you certain you want to proceed?
                </Text>
              </Flex>
            </ModalBody>
            <ModalFooter>
              <Button appearance="subtle" onClick={closeModal}>
                Cancel
              </Button>
              <LoadingButton
                isLoading={isLoading}
                appearance="primary"
                testId="billing-admin-remove-action-confirm-btn"
                onClick={handleConfirmClick}
              >
                Confirm
              </LoadingButton>
            </ModalFooter>
          </Modal>
        )}
      </ModalTransition>
    </>
  );
};
